import first from "lodash/first";
import { type FunctionComponent, useCallback, useMemo } from "react";
import { graphql, useFragment } from "react-relay";
import { findFirstNonEmptyString } from "scmp-sdk";

import { BaseLinkContextProvider } from "shared/components/common/base-link/context";
import { tracking } from "shared/data";
import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { getAbsoluteUrl, notEmpty } from "shared/lib/utils";

import { checkIsLiveArticle } from "scmp-app/components/article/article-render/live-article/helpers";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";
import type { focusArticleHomeFocusArticleQuery$key } from "scmp-app/queries/__generated__/focusArticleHomeFocusArticleQuery.graphql";

import { getArticleLogo, getArticleTitle, getPodcastTitle, getVideoTitle } from "./helpers";
import { Container, LiveDot, StyledContentItemHomeFocus, Title } from "./styles";

type Props = {
  className?: string;
  pageType: "homepage" | "section" | "subsection";
  reference: focusArticleHomeFocusArticleQuery$key;
};

type Settings = {
  custom_text?: string;
};

export const HomeFocusArticle: FunctionComponent<Props> = ({ className, pageType, reference }) => {
  const data = useFragment(
    graphql`
      fragment focusArticleHomeFocusArticleQuery on Query
      @argumentDefinitions(focusQueueName: { type: "String!" }) {
        focus: queue(filter: { name: $focusQueueName }) {
          settings
          items(first: 1) {
            edges {
              node {
                ...homeFocusContentItemContent
                ...helpersGetArticleTitleArticle
                ...helpersGetPodcastTitlePodcast
                ...helpersGetVideoTitleVideo
                ...helpersCheckIsLiveArticle
                ...helpersGetArticleLogoArticle
                ... on Content {
                  entityId
                  urlAlias
                }
              }
            }
          }
        }
      }
    `,
    reference,
  );

  const article = first(data?.focus?.items?.edges)?.node;

  const title = useMemo(() => {
    const customText = (data?.focus?.settings as Settings)?.custom_text ?? "";
    const videoTitle = getVideoTitle(article);
    const articleTitle = getArticleTitle(article);
    const podcastTitle = getPodcastTitle(article);

    return findFirstNonEmptyString(customText, podcastTitle, videoTitle, articleTitle, "Focus");
  }, [article, data?.focus?.settings]);

  const { isLive = false, isLiveArticle = false } = notEmpty(article)
    ? checkIsLiveArticle(article)
    : {};

  const displayLogo = (data?.focus?.settings as Settings)?.custom_text
    ? null
    : getArticleLogo(article);

  const impressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        block_section: title,
        list_of_articles: article?.entityId,
        page_type: pageType,
        widget_name: "flexi_unit",
      },
      subcategory: "widget",
    }),
    [article?.entityId, title, pageType],
  );

  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: impressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });

  if (!article) return null;

  return (
    <BaseLinkContextProvider
      customQueryParameters={{
        module: tracking.module.FlexiUnitFocus,
      }}
    >
      <Container className={className}>
        <Title>
          {isLiveArticle && isLive && <LiveDot $isLive={isLive} />}
          {displayLogo ?? <span>{title}</span>}
        </Title>
        <StyledContentItemHomeFocus
          onClick={entityId => {
            sendGA4Tracking({
              action: "click",
              category: "recirculation",
              customized_parameters: {
                article_id: entityId,
                block_section: title,
                destination_url: getAbsoluteUrl(article?.urlAlias),
                page_type: "homepage",
                widget_name: "flexi_unit",
              },
              subcategory: "widget",
            });
          }}
          ref={captureTrackImpressionEventTargetElement}
          reference={article}
          topicLinkVariant={{ type: "main" }}
        />
      </Container>
    </BaseLinkContextProvider>
  );
};

HomeFocusArticle.displayName = "HomeFocusArticle";
